import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { COPILOT_DISCLAIMER } from '../../copilot-shared/copilot-disclaimer';
import { COPILOT_DISCLAIMER_ENABLED } from '../../copilot-shared/copilot-disclaimer-visibility';
import { compareLegStrikeDesc, Leg, Solution } from './copilot-domain';
import { IHedgingGridCopilotFacade } from './copilot-facade';
import { HgCopilotTransactionsPopupComponent } from './hg-copilot-transactions-popup.component';
import { ComboNet, computeComboNet, formatMoney as formatComboMoney, ledgerPrice, orderPrice } from './copilot-pricing';
import { CloseAllState } from './copilot-ui-types';
import { LastQuoteCacheService } from '../../last-quote-cache.service';
import { MessageBusService } from '../../message-bus.service';
import { TradingInstrumentDisplayNameService } from '../../trading-instruments/trading-instrument-display-name.service';
import { QuoteDto } from '../../shell-communication/dtos/quote-dto.class';
import { DetectMethodChanges, isVoid } from '../../utils';

// Aggregate "Close All / Close Chosen Hedges" card (CR-20260619-001, Close-All increment). Renders BELOW
// the per-hedge close Solution cards on the CLOSE tab (only when there is more than one). It is a MASTER
// control: Preview/Apply act on EVERY child close at once (the orchestrator iterates the existing per-card
// facade calls — same net effect as actioning each card by hand), and while it owns them the child cards
// are locked (aggregateLocked). This component is PRESENTATIONAL: it emits previewAll/applyAll intents and
// builds the read-only Transactions view; all grid writes + master/child state live in the orchestrator.
//
// Default change detection (NOT OnPush), like the Solution card: the orchestrator mutates the shared
// aggregateState in place then calls detectChanges(); a default-CD card re-renders reliably.
@Component({
    selector: 'ets-hg-copilot-close-all-card',
    templateUrl: 'hg-copilot-close-all-card.component.html',
    styleUrls: ['./hg-copilot-close-all-card.component.scss']
})
export class HgCopilotCloseAllCardComponent implements OnInit, OnDestroy {

    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _lastQuoteCache: LastQuoteCacheService,
        private readonly _messageBus: MessageBusService,
        private readonly _displayNames: TradingInstrumentDisplayNameService
    ) {
    }

    // M8: the shared compliance disclaimer, rendered once under the shared aggregate action row.
    // A constant, never reassigned — exposed as a field only because templates cannot import.
    readonly disclaimer = COPILOT_DISCLAIMER;

    // CR-20260723-003: global visibility switch for the disclaimer above (client-requested disable).
    // HIDDEN, not removed - flip COPILOT_DISCLAIMER_ENABLED to re-show the disclaimer at all five sites.
    readonly disclaimerEnabled = COPILOT_DISCLAIMER_ENABLED;

    private readonly _unsubscriber = new Subject<void>();
    private _subscribedTickers: string[] = [];

    // The per-hedge close Solutions this aggregate covers (the active close tab's solutions). Re-subscribe
    // the live quotes whenever the set changes (a re-Run rebuilds them).
    @Input()
    set solutions(value: Solution[]) {
        this._solutions = value || [];
        this.resubscribeTickers();
    }
    get solutions(): Solution[] {
        return this._solutions;
    }
    private _solutions: Solution[] = [];

    // The grid facade — supplies the net-price normalizers (getNormFactor / readDefaultQty) and the
    // per-hedge Modified / Gone checks that gate Preview-All (disable-until-resolved).
    @Input() ctx: IHedgingGridCopilotFacade | null = null;

    // Target mode from the Close questionnaire — drives the title (All vs Chosen).
    @Input() targetKind: 'all' | 'hedges' = 'all';

    // The master state, owned by the orchestrator (single source of truth) and read here.
    @Input() aggregateState: CloseAllState = { previewed: false, applied: false };

    @Output() previewAll = new EventEmitter<void>();
    @Output() applyAll = new EventEmitter<void>();

    // Expand/collapse is OWNED by the orchestrator (so it can collapse the individual per-hedge cards to a
    // one-line summary while this card is open — freeing space for the detail). Read via @Input, request a
    // change via expandedChange.
    @Input() expanded = false;
    @Output() expandedChange = new EventEmitter<boolean>();

    @ViewChild(HgCopilotTransactionsPopupComponent) txPopup: HgCopilotTransactionsPopupComponent;

    // ---- titles / copy ----

    // Collapsed title — "Close \"All\"" / "Close \"Chosen\"" per target mode (matches the minimized sketch).
    get title(): string {
        return this.targetKind === 'all' ? 'Close "All"' : 'Close "Chosen"';
    }

    // The Transactions popup title (no quotes) — "Close All Hedges" / "Close Chosen Hedges".
    get aggregateDisplayName(): string {
        return this.targetKind === 'all' ? 'Close All Hedges' : 'Close Chosen Hedges';
    }

    // The expanded descriptive line (the aggregate analogue of the per-hedge "Generated from this hedge…").
    readonly descriptionLine = 'Closes Every Listed Hedge in One Action — One Closing Transaction Per Held Leg. No Suggestion Service is Called For a Close.';

    // ---- button labels + gating (mirror the per-hedge card's workflow at the master level) ----

    get previewLabel(): string {
        return this.aggregateState.previewed ? 'Un-Preview All' : 'Preview All';
    }

    get applyLabel(): string {
        return this.aggregateState.applied ? 'Un-Apply All' : 'Apply All';
    }

    // Apply follows Preview: only available once the batch is previewed (un-Apply always allowed).
    get applyDisabled(): boolean {
        return !this.aggregateState.previewed;
    }

    // Cannot un-Preview while applied (un-Apply first). Cannot Preview-All while any child is Modified /
    // Removed (owner ruling: disable until resolved) — only blocks the INITIAL preview, never the un-Preview.
    get previewDisabled(): boolean {
        if (this.aggregateState.applied) {
            return true;
        }
        if (!this.aggregateState.previewed && this.hasBlockingChild) {
            return true;
        }
        return false;
    }

    get transactionsDisabled(): boolean {
        return this.solutions.length === 0;
    }

    // ---- disable-until-resolved (owner ruling) ----

    // True when at least one child close cannot be previewed because its hedge was manually Modified or
    // Removed — Preview-All is disabled until the trader resolves it. Mirrors the per-card isModified /
    // isTargetGone checks against the same facade.
    get hasBlockingChild(): boolean {
        return this.blockingHedgeNames.length > 0;
    }

    // The display names of the hedges currently blocking Preview-All (for the tooltip).
    get blockingHedgeNames(): string[] {
        const ctx = this.ctx;
        if (!ctx) {
            return [];
        }
        const out: string[] = [];
        this.solutions.forEach(sol => {
            const groupIds = this.solutionGroupIds(sol);
            const modified = groupIds.some(g => ctx.isCopilotHedgeModified(g));
            const tickers = this.targetLegTickers(sol);
            const gone = tickers.length > 0 && ctx.isCopilotHedgeGone(tickers);
            if (modified || gone) {
                out.push(this.hedgeName(sol));
            }
        });
        return out;
    }

    // Tooltip when Preview-All is blocked (empty otherwise).
    get previewBlockedHint(): string {
        const names = this.blockingHedgeNames;
        if (names.length === 0) {
            return '';
        }
        return 'Resolve These Hedges First (Manually Modified or Removed): ' + names.join(', ');
    }

    // The distinct existing-hedge groupIds a close Solution targets (close legs carry groupId).
    private solutionGroupIds(sol: Solution): string[] {
        const out: string[] = [];
        const legs = (sol && sol.content) ? sol.content.legs : [];
        (legs || []).forEach(l => {
            if (l.groupId && out.indexOf(l.groupId) === -1) {
                out.push(l.groupId);
            }
        });
        return out;
    }

    // The option tickers of a Solution's target legs — matched against still-held book positions (the
    // hedge groupId is not stable across an Acquire rebuild; tickers are).
    //
    // Held-referencing legs only (close/decrease/increase), the same filter the per-card check applies:
    // a To-Open leg on a target hedge is expected to be unheld and proves nothing about the hedge's
    // existence. A no-op for this card today — every aggregated child is a Close, all legs close — but
    // the two copies of this derivation must not drift apart.
    private targetLegTickers(sol: Solution): string[] {
        const legs = (sol && sol.content) ? sol.content.legs : [];
        const out: string[] = [];
        (legs || []).forEach(l => {
            const referencesHeld = l.effect === 'close' || l.effect === 'decrease'
                || l.effect === 'increase' || l.effect === 'reverse';
            if (l.groupId && l.ticker && referencesHeld && out.indexOf(l.ticker) === -1) {
                out.push(l.ticker);
            }
        });
        return out;
    }

    // ---- per-hedge rendering (expanded) ----

    // The hedge's own display name (no "Close" prefix) for the sub-section header — the close legs carry it.
    hedgeName(sol: Solution): string {
        const legs = (sol && sol.content) ? sol.content.legs : [];
        const head = (legs || [])[0];
        if (head && !isVoid(head.hedgeLabel) && head.hedgeLabel !== '') {
            return head.hedgeLabel as string;
        }
        return (sol.content && sol.content.displayName) || '';
    }

    // The hedge's legs in the universal strike-DESC order (a COPY — never mutate the immutable content).
    sortedLegs(sol: Solution): Leg[] {
        const legs = (sol && sol.content) ? sol.content.legs : [];
        return (legs || []).slice().sort(compareLegStrikeDesc);
    }

    // Signed order quantity: sell legs negative, buy legs positive (same rule as the per-hedge card).
    signedQuantity(leg: Leg): number {
        return leg.side === 'sell' ? -leg.quantity : leg.quantity;
    }

    // Instrument display name from the RAW ticker — never a hand-built string (memory: ui-display-names).
    instrumentName(leg: Leg): string {
        return this._displayNames.getOptionDisplayName(leg.ticker);
    }

    // Per-leg cash-flow price: SELL receives premium (credit, +), BUY pays it (debit, -). Blank until priced.
    legSignedPrice(leg: Leg): number | undefined {
        const mid = this.quoteMid(leg.ticker);
        if (mid === undefined) {
            return undefined;
        }
        return leg.side === 'sell' ? mid : -mid;
    }

    // Per-hedge order-price Total row (mirrors the per-hedge card's expanded Total). Blank until priced.
    hedgeOrderPrice(sol: Solution): number | undefined {
        return orderPrice(this.comboNet(sol));
    }

    // ---- aggregate price (header) ----

    // The aggregate header price = the SUM of each child's LEDGER price (the per-card header value), via the
    // SAME shared math. All-or-nothing: blank ("—") until EVERY contributing hedge is fully priced.
    get summedLedgerPrice(): number | undefined {
        const defaultQty = this.ctx ? this.ctx.readDefaultQty() : undefined;
        let sum = 0;
        for (const sol of this.solutions) {
            const p = ledgerPrice(this.comboNet(sol), defaultQty);
            if (p === undefined) {
                return undefined;   // all-or-nothing across the batch
            }
            sum += p;
        }
        return sum;
    }

    private comboNet(sol: Solution): ComboNet | undefined {
        const legs = (sol && sol.content) ? sol.content.legs : [];
        return computeComboNet(
            legs,
            ticker => this.quoteMid(ticker),
            asset => this.ctx ? this.ctx.getNormFactor(asset) : 1
        );
    }

    private quoteMid(ticker: string): number | undefined {
        const q = this._lastQuoteCache.getLastQuote(ticker);
        if (isVoid(q)) {
            return undefined;
        }
        return (typeof q.mid === 'number' && !isNaN(q.mid)) ? q.mid : undefined;
    }

    // Dollar-formatted price: "$1.25" / "-$2.00"; blank ("—") when not yet priced.
    formatMoney(value: number | undefined): string {
        return formatComboMoney(value);
    }

    // ---- live quotes + change detection ----

    ngOnInit(): void {
        this.resubscribeTickers();
        // Every 'QuoteDto' bus tick forces a CD pass (decorator) so the price getters re-pull fresh mids.
        this._messageBus.of<QuoteDto[]>('QuoteDto')
            .pipe(takeUntil(this._unsubscriber))
            .subscribe(() => this.onQuoteTick());
    }

    ngOnDestroy(): void {
        if (this._subscribedTickers.length > 0) {
            this._lastQuoteCache.unsubscribeTickers(this._subscribedTickers);
            this._subscribedTickers = [];
        }
        this._unsubscriber.next();
        this._unsubscriber.complete();
    }

    // Empty body — the decorator forces the CD pass; the getters re-pull fresh mids from the cache.
    @DetectMethodChanges()
    onQuoteTick(): void {
    }

    // Request expand/collapse — the orchestrator owns the flag (it also collapses the individual cards).
    toggleExpanded(): void {
        this.expandedChange.emit(!this.expanded);
    }

    onPreview(): void {
        this.previewAll.emit();
    }

    onApply(): void {
        this.applyAll.emit();
    }

    // Build a synthetic Solution whose legs are the UNION of every child's close legs (each already carries
    // groupId/hedgeLabel/hedgeColor) and open the read-only Transactions popup. buildCopilotTransactionRows
    // groups by groupId, so it renders ONE Before/Transaction/After block per hedge automatically.
    onTransactions(): void {
        const allLegs: Leg[] = [];
        this.solutions.forEach(s => {
            const legs = (s.content && s.content.legs) ? s.content.legs : [];
            legs.forEach(l => allLegs.push(l));
        });
        const synthetic: Solution = {
            id: 'close-all',
            content: {
                skillId: '',
                skillUniqueId: '',
                displayName: this.aggregateDisplayName,
                shortDescription: '',
                extendedDescription: this.descriptionLine,
                legs: allLegs,
                // No block structure on a locally-generated close — the popup renders `legs` flat,
                // grouped by groupId. (Also the compile fix: `cards` became required when packages
                // shipped, and this synthetic literal was never updated.)
                cards: []
            },
            state: { previewed: false, applied: false, blocked: false }
        };
        this.txPopup.show(synthetic);
    }

    // Ref-count the union of all child leg tickers so live quotes stream; drop the previous set first.
    private resubscribeTickers(): void {
        const next: string[] = [];
        this._solutions.forEach(sol => {
            const legs = (sol.content && sol.content.legs) ? sol.content.legs : [];
            legs.forEach(l => {
                if (!isVoid(l.ticker) && next.indexOf(l.ticker) === -1) {
                    next.push(l.ticker);
                }
            });
        });
        // No-op when unchanged (avoid churn on every CD-triggering input write).
        if (this.sameTickers(next, this._subscribedTickers)) {
            return;
        }
        if (this._subscribedTickers.length > 0) {
            this._lastQuoteCache.unsubscribeTickers(this._subscribedTickers);
        }
        if (next.length > 0) {
            this._lastQuoteCache.subscribeTickers(next);
        }
        this._subscribedTickers = next;
    }

    private sameTickers(a: string[], b: string[]): boolean {
        if (a.length !== b.length) {
            return false;
        }
        for (let i = 0; i < a.length; i++) {
            if (a[i] !== b[i]) {
                return false;
            }
        }
        return true;
    }
}
